import React, { useState, useRef, useEffect } from "react"
import Image from "next/image"
import { FaStar, FaQuoteLeft } from "react-icons/fa"
import { s3Static } from "utils/constants"

// ─── Theme configuration ────────────────────────────────────────────
const themes = {
  light: {
    card: "bg-white/60 backdrop-blur-xl border border-white/70 shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(251,146,60,0.12)] hover:border-orange-200/60",
    cardOverlay: "bg-linear-to-br from-white/80 via-transparent to-orange-50/30",
    quoteIcon: "text-orange-300/40",
    reviewText: "text-gray-700",
    expandBtn: "text-orange-500 hover:text-orange-600",
    starActive: "text-amber-400",
    starInactive: "text-gray-200",
    ratingText: "text-gray-400",
    nameText: "text-gray-800",
    subtitleText: "text-gray-400",
    avatarBorder: "border-white/80",
    avatarFallbackBg: "bg-linear-to-br from-orange-400 to-amber-400",
    borderSeparator: "border-gray-100",
    spinnerBorder: "border-orange-400",
    endText: "text-gray-300",
  },
  dark: {
    card: "bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20",
    cardOverlay: "",
    quoteIcon: "text-orange-400/20",
    reviewText: "text-white/70",
    expandBtn: "text-orange-400 hover:text-orange-300",
    starActive: "text-amber-400",
    starInactive: "text-white/10",
    ratingText: "text-white/40",
    nameText: "text-white/80",
    subtitleText: "text-orange-400/60",
    avatarBorder: "border-white/20",
    avatarFallbackBg: "bg-linear-to-br from-orange-400 to-amber-400",
    borderSeparator: "border-white/5",
    spinnerBorder: "border-orange-400",
    endText: "text-white/20",
  },
}

// ─── ReviewCard (theme-aware, matches original TrainerCustomerReviews layout) ───
const ReviewCard = ({ review, index, expandedIndex, onToggleExpand, t, providerLabel }) => {
  const maxRating = [1, 2, 3, 4, 5]
  const reviewComment = review.reviewComment || ""
  const isExpanded = expandedIndex[index]
  const shortText = reviewComment.length > 240 ? reviewComment.slice(0, 240) + "..." : reviewComment

  return (
    <div className={`group relative rounded-2xl p-4 md:p-5 transition-all duration-300 ${t.card}`}>
      {/* Glass highlight overlay (light theme only) */}
      {t.cardOverlay && (
        <div className={`absolute inset-0 rounded-2xl pointer-events-none ${t.cardOverlay}`} />
      )}

      <div className="relative z-10">
        {/* Quote icon */}
        <FaQuoteLeft className={`text-2xl mb-3 ${t.quoteIcon}`} />

        {/* Review text with expand/collapse */}
        <p className={`text-sm leading-relaxed mb-4 whitespace-pre-wrap break-words ${t.reviewText}`}>
          {isExpanded ? reviewComment : shortText}
          {reviewComment.length > 240 && (
            <button
              onClick={() => onToggleExpand(index)}
              className={`ml-1 text-xs font-medium transition-colors ${t.expandBtn}`}
            >
              {isExpanded ? "Less" : "More"}
            </button>
          )}
        </p>

        {/* Rating stars */}
        <div className="flex items-center gap-1 mb-4">
          {maxRating.map((_, i) => (
            <FaStar
              key={i}
              className={`text-sm ${i < review.rating ? t.starActive : t.starInactive}`}
            />
          ))}
          <span className={`text-xs ml-1 ${t.ratingText}`}>
            {review.rating?.toFixed(1)}
          </span>
        </div>

        {/* Reviewer info */}
        <div className={`flex items-center gap-3 pt-3 border-t ${t.borderSeparator}`}>
          {review.profilePic ? (
            <div className={`relative w-10 h-10 rounded-full overflow-hidden border shrink-0 ${t.avatarBorder}`}>
              <Image
                src={review.profilePic}
                fill
                style={{ objectFit: "cover" }}
                alt={review.requestUserFullNm || "Reviewer"}
                sizes="40px"
              />
            </div>
          ) : review.avatar ? (
            <div className={`relative w-10 h-10 rounded-full overflow-hidden border shrink-0 ${t.avatarBorder}`}>
              <Image
                src={s3Static + review.avatar}
                alt={review.requestUserFullNm || "Reviewer"}
                width={40}
                height={40}
                className="object-cover"
              />
            </div>
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${t.avatarFallbackBg}`}>
              <span className="text-white font-bold text-sm">
                {review.requestUserFullNm?.charAt(0) || "?"}
              </span>
            </div>
          )}
          <div>
            <p className={`text-sm font-semibold ${t.nameText}`}>
              {review.requestUserFullNm}
            </p>
            {review.serviceProviderName && (
              <p className={`text-xs ${t.subtitleText}`}>
                {providerLabel || "Provider"}: {review.serviceProviderName}
              </p>
            )}
            {review.location && (
              <p className={`text-xs ${t.subtitleText}`}>
                {review.location}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── CustomerReviews (reusable, themed) ─────────────────────────────
export const CustomerReviews = ({
  testimonials,
  onLoadMore,
  isLoadingExternal = false,
  hasMoreExternal = true,
  theme = "light",
  providerLabel,
}) => {
  const [expanded, setExpanded] = useState({})
  const observerRef = useRef(null)
  const mobileScrollRef = useRef(null)

  const t = themes[theme] || themes.light

  const toggleExpand = (i) => {
    setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))
  }

  // Intersection observer for infinite scroll on mobile
  useEffect(() => {
    if (!observerRef.current || !onLoadMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreExternal && !isLoadingExternal) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(observerRef.current)
    return () => observer.disconnect()
  }, [onLoadMore, hasMoreExternal, isLoadingExternal])

  return (
    <>
      {/* Mobile: Horizontal swipe carousel (matches original TrainerCustomerReviews) */}
      <div className="md:hidden overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide" ref={mobileScrollRef}>
        <div className="flex gap-3" style={{ width: "max-content" }}>
          {testimonials.map((review, i) => (
            <div key={i} className="w-[80vw] max-w-xs shrink-0">
              <ReviewCard
                review={review}
                index={i}
                expandedIndex={expanded}
                onToggleExpand={toggleExpand}
                t={t}
                providerLabel={providerLabel}
              />
            </div>
          ))}
          {/* Auto-load trigger at end of scroll */}
          <div ref={observerRef} className="w-4 shrink-0" />
          {isLoadingExternal && (
            <div className="flex items-center justify-center w-12 shrink-0">
              <div className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${t.spinnerBorder}`} />
            </div>
          )}
        </div>
      </div>

      {/* Desktop: Grid layout */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {testimonials.map((review, i) => (
          <ReviewCard
            key={i}
            review={review}
            index={`desktop-${i}`}
            expandedIndex={expanded}
            onToggleExpand={toggleExpand}
            t={t}
            providerLabel={providerLabel}
          />
        ))}
      </div>
    </>
  )
}
